import { getUserMediaWithFallback } from '@/callMedia';

// Mic/camera/screen share/settings/full-screen control bar behavior shared by the studio editor and the guest page.
//
// The consuming component owns: `ownStream`, `callSession` (a CallSession or null), `error`, and a template ref
// named `stageWrap` on whatever should go full screen - and is expected to call `notifyOwnStream()`
// itself after anything else changes `ownStream` (e.g. the initial device-check handoff).
export default {
	data() { return {
		micMuted: false,
		speakersMuted: false,
		cameraOff: true,
		cameraPending: false,
		showCallSettings: false,
		isFullscreen: false,
		devices: [],
		selectedCamera: null,
		selectedMic: null,
		audioSettings: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
		ownStreamListeners: [],
		screenSharing: false,
		screenStream: null,
	}},
	computed: {
		// Firefox lists a device without an id until that kind of device has been granted; it can't be selected anyway.
		cameraOptions() {
			return [
				{ value: null, text: 'Default camera' },
				...this.devices.filter(d => d.kind === 'videoinput' && d.deviceId).map(d => ({ value: d.deviceId, text: d.label || `Camera ${d.deviceId.slice(0, 8)}` })),
			];
		},
		micOptions() {
			return [
				{ value: null, text: 'Default microphone' },
				...this.devices.filter(d => d.kind === 'audioinput' && d.deviceId).map(d => ({ value: d.deviceId, text: d.label || `Mic ${d.deviceId.slice(0, 8)}` })),
			];
		},
	},
	mounted() {
		document.addEventListener('fullscreenchange', this.onFullscreenChange);
	},
	beforeDestroy() {
		document.removeEventListener('fullscreenchange', this.onFullscreenChange);
	},
	methods: {
		notifyOwnStream() {
			for(const cb of this.ownStreamListeners) cb(this.ownStream);
		},
		toggleMic() {
			this.micMuted = !this.micMuted;
			this.ownStream?.getAudioTracks().forEach(t => { t.enabled = !this.micMuted; });
			this.callSession?.setMicMuted(this.micMuted);
			this.notifyOwnStream(); // layers showing us re-check the muted state
		},
		// What we hear from the call (callAudio.js) - not our own mic.
		toggleSpeakers() {
			this.speakersMuted = !this.speakersMuted;
			this.callAudio?.setMuted(this.speakersMuted);
		},
		// Turning the camera on for the first time (it starts off, per the device check) means actually
		// requesting it here - re-enabling a track that was never acquired isn't possible.
		async toggleCamera() {
			this.cameraOff = !this.cameraOff;
			if(this.cameraOff) {
				await this.dropVideo();
				return;
			}
			if(this.ownStream?.getVideoTracks().length) {
				this.ownStream.getVideoTracks().forEach(t => { t.enabled = true; });
				return;
			}
			this.cameraPending = true;
			try {
				await this.restartOwnStream();
			} catch(e) {
				console.error('Enable camera error', e);
				this.cameraOff = true;
				this.error = e;
			} finally {
				this.cameraPending = false;
			}
		},
		// Off means off: the camera is released and no picture is sent at all, so everyone else sees "no camera"
		// instead of a black rectangle. (A disabled track keeps sending black frames.)
		async dropVideo() {
			const oldStream = this.ownStream;
			if(!oldStream?.getVideoTracks().length) return;
			this.ownStream = new MediaStream(oldStream.getAudioTracks());
			if(this.callSession) await this.callSession.replaceStream(this.ownStream);
			oldStream.getVideoTracks().forEach(t => t.stop());
			this.notifyOwnStream();
		},
		audioConstraints() {
			return {
				...(this.selectedMic ? { deviceId: { exact: this.selectedMic } } : {}),
				echoCancellation: this.audioSettings.echoCancellation,
				noiseSuppression: this.audioSettings.noiseSuppression,
				autoGainControl: this.audioSettings.autoGainControl,
			};
		},
		mediaConstraints() {
			return {
				video: this.cameraOff ? false : (this.selectedCamera ? { deviceId: { exact: this.selectedCamera } } : true),
				audio: this.audioConstraints(),
			};
		},
		// Re-acquires the camera/mic (device or processing change) and hands the new stream to the running
		// publish session via replaceStream, rather than dropping and rejoining it.
		async restartOwnStream() {
			const oldStream = this.ownStream;
			this.ownStream = await getUserMediaWithFallback(this.mediaConstraints());
			this.ownStream.getAudioTracks().forEach(t => { t.enabled = !this.micMuted; });
			this.ownStream.getVideoTracks().forEach(t => { t.enabled = !this.cameraOff; });
			if(this.callSession) await this.callSession.replaceStream(this.ownStream);
			oldStream?.getTracks().forEach(t => t.stop());
			this.notifyOwnStream();
		},
		async onCallSettingsDeviceChange() {
			try {
				await this.restartOwnStream();
				navigator.mediaDevices.enumerateDevices().then(d => { this.devices = d; }).catch(() => {});
			} catch(e) {
				console.error('Change devices error', e);
				this.error = e;
			}
		},
		// Fullscreens just the canvas itself (the `stageWrap` ref) - no toolbar, no side panels - scaled to
		// fit the screen with zero padding, so a canvas at the screen's own resolution renders exactly 1:1.
		toggleFullscreen() {
			if(document.fullscreenElement) document.exitFullscreen();
			else this.$refs.stageWrap?.requestFullscreen?.().catch(e => console.error('Fullscreen error', e));
		},
		onFullscreenChange() {
			this.isFullscreen = !!document.fullscreenElement;
		},
		async toggleScreenShare() {
			if(this.screenSharing) { this.stopScreenShare(); return; }
			let stream;
			try {
				// Sound is off by default in the picker in most browsers (Chrome: "Share tab audio" / system audio), and unsupported
				// in Firefox - and it's raw program audio, so none of the voice processing a mic gets.
				stream = await navigator.mediaDevices.getDisplayMedia({
					video: true,
					audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
					systemAudio: 'include',
				});
			} catch(e) {
				if(e.name !== 'NotAllowedError') this.error = e;
				return;
			}
			try {
				stream.getVideoTracks()[0].addEventListener('ended', () => { if(this.screenSharing) this.stopScreenShare(); });
				this.screenStream = stream;
				if(!stream.getAudioTracks().length) this.$bvToast.toast('Your browser shared the screen without its sound. Chrome can share a tab\'s (or the system\'s) audio - tick the audio option in its picker. Firefox can\'t share screen audio at all.', { title: 'No screen audio', variant: 'warning', solid: true, autoHideDelay: 10000 });
				this.screenSharing = true;
				await this.callSession.startScreenShare(stream);
			} catch(e) {
				console.error('Screen share error', e);
				this.error = e;
				stream.getTracks().forEach(t => t.stop());
				this.stopScreenShare();
			}
		},
		stopScreenShare() {
			this.screenStream?.getTracks().forEach(t => t.stop());
			this.screenStream = null;
			this.screenSharing = false;
			this.callSession?.stopScreenShare();
		},
	},
};
